import React from 'react';
import { ArrowUpCircle } from 'lucide-react';
import { BannerContainer } from '../BannerContainer';
import { BannerContent } from '../BannerContent';
import { BannerActions } from '../BannerActions';
import { UsageProgressCard } from '../UsageProgressCard';
import { FeaturesGrid } from '../FeaturesGrid';
import { BANNER_THEMES, FEATURE_SETS, ACTION_CONFIGS } from '../config/bannerConfig';
import { BannerType, UsageDetails } from '../../../types/subscription';
import { getUsageDescription } from '../utils/bannerUtils';

interface StandardBannerProps {
  type: 'limit_reached' | 'upgrade_recommendation';
  usage?: UsageDetails;
  planName?: string;
  onDismiss?: () => void;
  className?: string;
}

export const StandardBanner: React.FC<StandardBannerProps> = ({
  type,
  usage,
  planName,
  onDismiss,
  className
}) => {
  const theme = BANNER_THEMES[type];
  const features = FEATURE_SETS[type];
  const actions = ACTION_CONFIGS[type];

  // Generate dynamic description based on usage for limit_reached
  const dynamicDescription = (type === 'limit_reached' && usage) 
    ? getUsageDescription(usage, planName)
    : theme.description;

  return (
    <BannerContainer
      variant="gradient"
      theme={theme.theme}
      onDismiss={onDismiss}
      className={className}
    >
      <BannerContent
        layout="centered"
        emoji={theme.emoji}
        emojiAnimation={theme.emojiAnimation}
        title={theme.title}
        description={dynamicDescription}
        titleColor={theme.titleColor}
        descriptionColor={theme.descriptionColor}
      >
        {/* Usage Progress for limit_reached */}
        {type === 'limit_reached' && usage && (
          <UsageProgressCard
            usage={usage}
            variant="detailed"
            theme={type === 'limit_reached' ? "red" : "blue"}
          />
        )}

        {/* Action buttons */}
        <BannerActions
          primary={{
            ...actions.primary,
            icon: <ArrowUpCircle className="h-5 w-5 mr-2" />,
            onClick: onDismiss
          }}
          secondary={{
            ...actions.secondary,
            onClick: onDismiss
          }}
        />

        {/* Plan benefits preview */}
        <FeaturesGrid 
          features={features}
          variant={type === 'limit_reached' ? "dark" : "dark"}
          borderColor={type === 'limit_reached' ? "border-red-200" : "border-purple-200"}
          textColor={type === 'limit_reached' ? "text-red-100" : "text-purple-100"}
        />
      </BannerContent>
    </BannerContainer>
  );
};